/* ============================================================
   Plan — what's late, what's waiting for a day, and the timeline, under a
   header and week strip. The undated piles you pull FROM are drawers; the
   dated days you pull INTO are plain labels over cards. Picking a day in the
   strip jumps to its section, or to that day on Today when nothing is
   scheduled there yet.
   ============================================================ */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { addDaysStr, dayLabel, overdueLabel, todayStr, useStore, useWeekStart, type Task } from '@habishine/client-core';

import { Capture } from '../components/Capture';
import { CardGroup, DayLabel, GhostAdd, SectionDrawer, TaskRow } from '../components/rows';
import { SortableList } from '../components/Sortable';
import { Page, PageHeader } from '../components/ui';
import { WeekStrip } from '../components/WeekStrip';
import { CAPTURE_INPUT_ID } from '../components/Capture';
import { useEditors } from '../shell/Editors';

export function PlanPage() {
  const store = useStore();
  const { openTask } = useEditors();
  const { weekStartsOn } = useWeekStart();
  const navigate = useNavigate();
  const today = todayStr();
  const [selected, setSelected] = useState(today);
  const [overdueOpen, setOverdueOpen] = useState(true);
  const [backlogOpen, setBacklogOpen] = useState(false);

  const late = store.overdue();
  const later = store.backlog();
  const ahead = store.upcoming();
  const byDay = useMemo(() => {
    const m = new Map<string, Task[]>();
    for (const t of ahead) {
      const d = t.scheduledFor ?? '';
      m.set(d, [...(m.get(d) ?? []), t]);
    }
    return [...m.entries()];
  }, [ahead]);
  const dayCounts = useMemo(() => Object.fromEntries(byDay.map(([d, ts]) => [d, ts.length])), [byDay]);

  const pick = (day: string) => {
    setSelected(day);
    const el = document.getElementById(`day-${day}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else if (day !== today) navigate(`/today?d=${day}`);
  };

  const rows = (list: Task[], dated: boolean) => (
    <SortableList
      items={list}
      onMove={(from, to) => store.moveTask(list, from, to)}
      render={(t, i) => (
        <TaskRow
          task={t}
          first={i === 0}
          hue={store.hueForTask(t)}
          projectNote={store.projectForTask(t)?.name}
          tagNote={store.tagsOfTask(t).map((g) => g.name)}
          dateNote={dated && t.scheduledFor ? overdueLabel(t.scheduledFor) : undefined}
          onOpen={() => openTask(t.id, t.scheduledFor ?? today)}
          onComplete={() => store.completeItem(t.id)}
        />
      )}
    />
  );

  const sub = `${ahead.length} scheduled ahead${late.length ? ` · ${late.length} overdue` : ''}${later.length ? ` · ${later.length} in the backlog` : ''}`;

  return (
    <Page className="screen-in">
      <PageHeader hue="blue" eyebrow="The week ahead" title="Plan" sub={sub} />
      <WeekStrip selected={selected} onSelect={pick} dayCounts={dayCounts} weekStartsOn={weekStartsOn} />
      <Capture className="mt-4" defaultToday={false} placeholder='Add to the backlog — or give it a day: "friday", "oct 20 9am"' />

      {late.length > 0 ? (
        <SectionDrawer index={1} label="Overdue" sub="Needs a new day" icon="corner-up-left" tone="orange" count={late.length} open={overdueOpen} onToggle={() => setOverdueOpen((o) => !o)}>
          {rows(late, true)}
        </SectionDrawer>
      ) : null}
      {later.length > 0 ? (
        <SectionDrawer index={2} label="Backlog" sub="Unfiled, waiting for a day" icon="list" tone="blue" count={later.length} open={backlogOpen} onToggle={() => setBacklogOpen((o) => !o)}>
          {rows(later, false)}
        </SectionDrawer>
      ) : null}

      {byDay.map(([day, tasks], i) => (
        <section key={day} id={`day-${day}`} className="scroll-mt-6">
          <DayLabel sub={`${tasks.length}`}>{dayLabel(day)}</DayLabel>
          <CardGroup index={Math.min(i + 3, 10)}>{rows(tasks, false)}</CardGroup>
        </section>
      ))}
      {byDay.length === 0 ? (
        <GhostAdd label={`Nothing scheduled ahead — plan something for ${dayLabel(addDaysStr(today, 1)).split(' · ')[1]?.toLowerCase() ?? 'tomorrow'}`} onClick={() => document.getElementById(CAPTURE_INPUT_ID)?.focus()} />
      ) : null}
    </Page>
  );
}
